import { ART_CATS, ART_URL, apiGetCall } from '@/api/apiDispatcher';
import { startTransition, useCallback, useEffect, useRef, useState } from 'react';

import ArticleExcerpt from './ArticleExcerpt';
import ArticlesSearchForm from './ArticlesSearchForm';
import CustomPagination from '@/components/_common/Pagination';
import styled from 'styled-components';
import { useLangStore } from '@/store/lang-store';

const Articles = ({ text, length, routeMap, $columns }) => {
  const [dataArticles, setDataArticles] = useState();
  const [pagination, setPagination] = useState();
  const [searchText, setSearchText] = useState('');
  const [filtered, setFiltered] = useState('');
  const [isMounted, setIsMounted] = useState(false);
  const { langStore } = useLangStore();
  const wrapperRef = useRef(null);

  // Differer le fetch des articles jusqu'a ce que la section approche du viewport.
  // Sur la home, la liste est sous la ligne de flottaison et n'est pas dans le HTML
  // SSR : la charger au scroll libere le reseau + le thread principal pendant la
  // fenetre critique du LCP, sans aucune perte SEO. Sur les pages ou la liste est
  // au-dessus de la ligne de flottaison (actualites), l'observer se declenche
  // immediatement. rootMargin pre-charge 400px avant l'arrivee a l'ecran.
  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      startTransition(() => {
        setIsMounted(true);
      });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          startTransition(() => {
            setIsMounted(true);
          });
          observer.disconnect();
        }
      },
      { rootMargin: '400px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const searchApi = useCallback(
    (page = 1) => {
      if (!isMounted) return;

      const activeLang = langStore || 'nl-BE'; // Default fallback

      apiGetCall(ART_URL, activeLang, {
        limit: length ?? '',
        maxLevel: 6,
        minLevel: 5,
        keyword: searchText,
        category: ART_CATS[activeLang.substring(0, 2)],
        page: page,
      }).then((data) => {
        startTransition(() => {
          setDataArticles(data?.data);
          setPagination(data?.pagination);
        });
      });
    },
    [langStore, searchText, length, isMounted]
  );

  // Clear articles when language changes so the next effect re-fetches in the new language
  useEffect(() => {
    setDataArticles(undefined);
  }, [langStore]);

  useEffect(() => {
    if (isMounted && !dataArticles) {
      searchApi();
    }
  }, [searchApi, isMounted, dataArticles]);

  const searchArticles = (event) => {
    event.preventDefault();
    if (searchText.length > 2) {
      searchApi();
      startTransition(() => {
        setFiltered(true);
      });
    } else {
      startTransition(() => {
        setSearchText('');
      });
      if (filtered) {
        searchApi();
        startTransition(() => {
          setFiltered(false);
        });
      }
    }
  };

  const updatePage = (p) => {
    searchApi(p);
    window.scrollTo(0, 0, 'smooth');
  };

  return (
    <Wrapper ref={wrapperRef} $columns={$columns}>
      {!length &&
        ArticlesSearchForm({
          searchText,
          setSearchText,
          searchArticles,
          filtered,
        })}
      {!dataArticles &&
        // Skeleton placeholders: reserve vertical space while articles load (prevents CLS)
        Array.from({ length: Number(length) || 4 }).map((_, i) => (
          <SkeletonArticle key={`skeleton_${i}`} aria-hidden='true' />
        ))}
      {dataArticles &&
        dataArticles?.map((item, ide) => (
          <Article key={`${item.id}__${ide}`}>
            {Object.keys(item)?.map((keyName, id) => {
              if (keyName === 'excerpt') {
                const trimmedText = item[keyName].substring(0, 350);
                const completeText = item[keyName].length > 350;
                const displayText = completeText ? `${trimmedText}...` : item[keyName];
                const html = { __html: displayText };

                return (
                  <div key={`excerpt__${keyName}_${id}`}>
                    <ArticleExcerpt
                      item={item}
                      keyName={keyName}
                      routeMap={routeMap}
                      id={id}
                      text={text}
                      html={html}
                    />
                  </div>
                );
              }
              return null;
            })}
          </Article>
        ))}
      {pagination && !length && (
        <CustomPagination
          pageSize={pagination?.perPage}
          onChange={updatePage}
          current={pagination?.currentPage}
          total={pagination?.total}
        />
      )}
    </Wrapper>
  );
};

export default Articles;

const Wrapper = styled.div.attrs({
  className: 'Wrapper',
})`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 0;
  padding-bottom: 0;
  @media only screen and (min-width: 768px) {
    grid-template-columns: ${({ $columns }) => ($columns ? '1fr 1fr' : '1fr')};
    gap: 24px;
    padding-bottom: 24px;
  }
`;

const Article = styled.article.attrs({
  className: 'Article',
})`
  border-radius: 5px;
  margin-bottom: 0;

  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  box-shadow: 0 5px 30px rgba(0, 0, 0, 0.2);
  padding: 16px;
  @media screen and (min-width: 768px) {
    padding: 24px;
    gap: 24px;
  }
`;

const SkeletonArticle = styled.div.attrs({
  className: 'Article',
})`
  border-radius: 5px;
  min-height: 360px;
  box-shadow: 0 5px 30px rgba(0, 0, 0, 0.2);
  background: linear-gradient(90deg, #f0f0f0 25%, #fafafa 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-pulse 1.5s ease-in-out infinite;

  @keyframes skeleton-pulse {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;
